import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import {
  getRetailMediaCounterpartID,
  isAdvertiser,
  isPublisher
} from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useGetFetchQuery } from '~/hooks/useGetFetchQuery'
import useQueryParams from '~/hooks/useQuery'

import {
  getMarketplaceBalance,
  getMarketplaceBalanceKey
} from '../services/marketplace/balance'
import { usePaymentTypes } from './usePaymentTypes'

export const useMarketplaceBalance = () => {
  const { isWhiteLabel } = useAppInfo()
  const publisherId = useQueryParams().get('rmid') as PublisherId
  const requestKey = getRetailMediaCounterpartID()

  const { data: paymentTypes } = usePaymentTypes()

  const queryKey = [getMarketplaceBalanceKey, requestKey, publisherId]

  const cachedData = useGetFetchQuery<GetMarketplaceBalanceResponse>(queryKey)

  const enabled = useMemo(() => {
    const hasMarketPlaceEnabled =
      Array.isArray(paymentTypes) &&
      paymentTypes.some(
        paymentType =>
          paymentType?.type === 'marketplace' && paymentType?.active
      )

    return (
      hasMarketPlaceEnabled &&
      (isWhiteLabel ||
        (!isWhiteLabel && isAdvertiser() && !!publisherId) ||
        (!isWhiteLabel && isPublisher()))
    )
  }, [isWhiteLabel, publisherId, paymentTypes])

  const {
    data: response = null,
    isPending,
    error
  } = useQuery({
    enabled,
    queryKey,
    queryFn: async () => {
      const params = isWhiteLabel ? undefined : { publisher_id: publisherId }
      const { data } = await getMarketplaceBalance(params)
      return data
    }
  })

  const data = cachedData || response

  return {
    isPending: isPending && enabled,
    data,
    error,
    queryKey
  }
}
